import Form from "@/components/Forms/Form";
import { FieldTypes } from "@/components/Forms/FieldTypes";
import { Box, Typography } from "@mui/material";
import { FormField } from "@/components/Forms/FormField";
import { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { matchSorter } from "match-sorter";
import Loading from "@/components/Widgets/Loading";

interface Disease {
  id: string;
  name: string;
  aka: string;
  generalCategory: string;
  drugContraindications: string; // TODO TH: enumize this
}

interface Medication {
  id: string;
  genericName: string;
  brandNames: string;
  class: string; // TODO TH: enumize this
}

interface Allergy {
  id: string;
  name: string;
  aka: string;
}

type PatientDataItem = string | { id?: string; name?: string; value?: string };

interface FilterFnArgs {
  inputValue: string;
}

const RegistrationMedicalHistory = (props: {
  onNext: (values: Record<string, unknown>) => void;
  /** Omitted: no Back button (the first health-history page after enrollment payment). */
  onBack?: (values: Record<string, unknown>) => void;
  /** The final page's button, when this is the last step. */
  submitButtonText?: string;
  values: Record<string, unknown>;
}) => {
  const api = useApi();
  const { onNext, onBack, values, submitButtonText } = props;
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);

  const pastMedicalHistoryfilterOptions = (
    options: Disease[],
    { inputValue }: FilterFnArgs,
  ) =>
    matchSorter(options, inputValue, {
      keys: [
        "name",
        { maxRanking: matchSorter.rankings.STARTS_WITH, key: "aka" },
      ],
    });

  const medicationsfilterOptions = (
    options: Medication[],
    { inputValue }: FilterFnArgs,
  ) =>
    matchSorter(options, inputValue, {
      keys: [
        "genericName",
        { maxRanking: matchSorter.rankings.STARTS_WITH, key: "brandNames" },
      ],
    });

  const allergiesfilterOptions = (
    options: Allergy[],
    { inputValue }: FilterFnArgs,
  ) =>
    matchSorter(options, inputValue, {
      keys: [
        "name",
        { maxRanking: matchSorter.rankings.STARTS_WITH, key: "aka" },
      ],
    });

  const getMedications = async () => {
    const result = await api.get<Medication[]>(`/data/medications`);
    return result.data;
  };

  const getDiseases = async () => {
    const result = await api.get<Disease[]>(`/data/diseases`);
    return result.data;
  };

  const getAllergies = async () => {
    const result = await api.get<Allergy[]>(`/data/allergies`);
    return result.data;
  };

  useEffect(() => {
    const fetchMedicationList = async () => {
      const medicationList = await getMedications();
      setMedications(medicationList);
    };

    fetchMedicationList();

    const fetchDiseaseList = async () => {
      const diseaseList = await getDiseases();
      setDiseases(diseaseList);
    };

    fetchDiseaseList();

    const fetchAllergyList = async () => {
      const allergiesList = await getAllergies();
      setAllergies(allergiesList);
    };

    fetchAllergyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFormFields = (): FormField[] => {
    return [
      {
        id: "pastMedicalHistory",
        text: "Past Medical History",
        columns: 12,
        value: (values.pastMedicalHistory as PatientDataItem[]) || [],
        type: FieldTypes.MultipleAutocomplete,
        choices: diseases.map((disease: Disease) => {
          return {
            text: disease.name,
            value: disease.id,
            ...disease,
          };
        }),
        filterOptions: pastMedicalHistoryfilterOptions,
      },
      {
        id: "pastMedicalHistoryOther",
        text: "If you do not see a condition you've been diagnosed with, please write it in",
        columns: 12,
        value: (values.pastMedicalHistoryOther as string) || "",
        type: FieldTypes.MultiLineText,
        rows: 3,
      },
      {
        id: "currentMedications",
        text: "Current Medications",
        columns: 12,
        value: (values.currentMedications as PatientDataItem[]) || [],
        type: FieldTypes.MultipleAutocomplete,
        choices: medications.map((medication) => ({
          text: medication.brandNames.length
            ? `${medication.genericName} (${medication.brandNames})`
            : medication.genericName,
          value: medication.id,
          ...medication,
        })),
        filterOptions: medicationsfilterOptions,
      },
      {
        id: "allergies",
        text: "Allergies",
        columns: 12,
        value: (values.allergies as PatientDataItem[]) || [],
        type: FieldTypes.MultipleAutocomplete,
        choices: allergies.map((allergy) => ({
          text: allergy.name,
          value: allergy.id,
          ...allergy,
        })),
        filterOptions: allergiesfilterOptions,
      },
    ];
  };

  if (
    diseases.length === 0 ||
    medications.length === 0 ||
    allergies.length === 0
  ) {
    return <Loading />;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          color: "text.primary",
          fontWeight: 700,
          mb: 4,
        }}
      >
        Medical History
      </Typography>
      <Form
        fieldVariant="standard"
        fields={getFormFields()}
        onSubmit={onNext}
        submitButtonText={submitButtonText ?? "Next"}
        onCancel={onBack}
        cancelButtonText="Back"
      />
    </Box>
  );
};

export default RegistrationMedicalHistory;
